import { Layout, Menu, Card, theme, type MenuProps } from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { controlHeightTokens, marginTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';

const { Sider, Content } = Layout;

interface CustomerTabsProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  children: React.ReactNode;
}

const CustomerTabs = ({ activeSection, onSectionChange, children }: CustomerTabsProps) => {
  const { t } = useTranslation('customers');
  const { token } = theme.useToken();
  const elevation = useElevation();

  const currentColorScheme = {
    headerAccent: token.colorPrimary,
    cardShadow: token.boxShadow,
    borderColor: token.colorBorderSecondary,
    highlightBg: token.colorPrimaryBg,
  };

  const currentFontSizes = {
    menuItem: token.fontSizeLG,
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'primary',
      icon: <FileTextOutlined />,
      label: t('customer.tabs.primaryInfo'),
    },
    {
      key: 'financial',
      icon: <DollarOutlined />,
      label: t('customer.tabs.financialInfo'),
    },
    {
      key: 'transaction',
      icon: <FileTextOutlined />,
      label: t('customer.sections.transactionHistory'),
    },
    {
      key: 'preference',
      icon: <SettingOutlined />,
      label: t('customer.tabs.preference'),
    },
  ];

  return (
    <Card
      style={{
        ...elevation.card,
        padding: 0,
        overflow: 'hidden',
      }}
      styles={{ body: { padding: 0 } }}
    >
      <Layout style={{ backgroundColor: 'transparent' }}>
        <Sider
          width={248}
          theme="light"
          style={{
            borderRight: `1px solid ${currentColorScheme.borderColor}`,
            padding: 0,
            backgroundColor: 'transparent',
          }}
        >
          {/* Custom CSS for menu item heights */}
          <style>
            {`
              .customer-detail-menu {
                padding: 0 12px !important;
              }
              .customer-detail-menu .ant-menu-item {
                height: ${controlHeightTokens.controlHeightXL}px !important;
                line-height: ${controlHeightTokens.controlHeightXL}px !important;
                margin: 4px 0 !important;
                /* 12px, not 16 — the item chrome plus AntD's icon gap left the label
                   column too narrow for 「Bank Payment Details」 / 「Financial
                   Information」 at this 248px rail. Trimmed to the marginSM rung so
                   every label fits after the Menu icon gap moved to marginXS (8px);
                   see the Menu.iconMarginInlineEnd note in styles/theme.ts. */
                padding-left: ${marginTokens.marginSM}px !important;
                padding-right: ${marginTokens.marginSM}px !important;
                display: flex !important;
                align-items: center !important;
                border-radius: ${radiusTokens.card}px !important;
                width: 100% !important;
                font-size: ${currentFontSizes.menuItem}px !important;
              }
              .customer-detail-menu .ant-menu-item-selected {
                background-color: ${currentColorScheme.highlightBg} !important;
                color: ${currentColorScheme.headerAccent} !important;
              }
              .customer-detail-menu .ant-menu-item:hover {
                background-color: ${token.colorBgTextHover} !important;
              }
              .customer-detail-menu .ant-menu-item .ant-menu-item-icon {
                font-size: ${currentFontSizes.menuItem}px !important;
              }
              .customer-detail-menu .ant-menu-item .ant-menu-title-content {
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                font-size: ${currentFontSizes.menuItem}px !important;
              }
            `}
          </style>
          <Menu
            mode="inline"
            selectedKeys={[activeSection]}
            items={menuItems}
            onClick={({ key }) => onSectionChange(key)}
            className="customer-detail-menu"
            style={{ borderRight: 'none', backgroundColor: 'transparent' }}
          />
        </Sider>
        <Content style={{ padding: '24px', minHeight: '400px', backgroundColor: 'transparent' }}>
          {children}
        </Content>
      </Layout>
    </Card>
  );
};

export default CustomerTabs;
